import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../../services/api';
import TransactionPasswordModal from '../common/TransactionPasswordModal';
import '../../styles/admin/ChangePassword.css';
import { useDesktopViewport } from '../../hooks/useDesktopViewport';

const ChangePassword = () => {
    useDesktopViewport();
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [pendingPasswordData, setPendingPasswordData] = useState(null);
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [logoutTimer, setLogoutTimer] = useState(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        // Clear error when user starts typing again
        if (error) setError('');
        if (success) setSuccess('');
    };

    const togglePasswordVisibility = (field) => {
        setShowPassword({
            ...showPassword,
            [field]: !showPassword[field]
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Clear previous errors
        setError('');
        
        // Validate current password is not empty
        if (!formData.currentPassword.trim()) {
            setError('Please enter your current password');
            return;
        }
        
        // Validate new password
        if (!formData.newPassword) {
            setError('Please enter a new password');
            return;
        }
        
        // Validate passwords match
        if (formData.newPassword !== formData.confirmPassword) {
            setError('New password and confirm password do not match');
            return;
        }
        
        // Validate password length
        if (formData.newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }
        
        // Store password data and show transaction password modal
        setPendingPasswordData({
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword,
            confirmPassword: formData.confirmPassword
        });
        setShowTransactionModal(true);
    };

    const handleTransactionVerified = async (tempToken) => {
        setShowTransactionModal(false);
        localStorage.setItem('transactionToken', tempToken);
        
        await executeChangePassword();
    };

    const executeChangePassword = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await userAPI.changeAdminPassword({
                currentPassword: pendingPasswordData.currentPassword,
                newPassword: pendingPasswordData.newPassword,
                confirmPassword: pendingPasswordData.confirmPassword
            });
            
            if (response.success) {
                setSuccess(response.message);
                setFormData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                
                // Clear any existing timer
                if (logoutTimer) {
                    clearTimeout(logoutTimer);
                }
                
                // Set timer for auto logout
                const timer = setTimeout(() => {
                    logout();
                    navigate('/login');
                }, 3000);
                
                setLogoutTimer(timer);
            }
        } catch (err) {
            // Handle different error status codes
            console.log('Error response:', err.response);
            
            const status = err.response?.status;
            const message = err.response?.data?.message;
            
            if (status === 401) {
                setError('Current password is incorrect. Please try again.');
            } else if (status === 400) {
                setError(message || 'Invalid request. Please check your input.');
            } else {
                setError(message || 'Failed to change password. Please try again.');
            }
            
            // Clear transaction token on error
            localStorage.removeItem('transactionToken');
            
            // Don't redirect, stay on page
        } finally {
            setLoading(false);
            setPendingPasswordData(null);
        }
    };

    const getPasswordStrength = (password) => {
        if (!password) return 0;
        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        return strength;
    };

    const strength = getPasswordStrength(formData.newPassword);
    const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    const strengthColors = ['#ef4444', '#f59e0b', '#eab308', '#10b981', '#059669'];

    // Cleanup timer on component unmount
    useEffect(() => {
        return () => {
            if (logoutTimer) {
                clearTimeout(logoutTimer);
            }
        };
    }, [logoutTimer]);

    return (
        <div className="cp-container">
            {/* Transaction Password Modal */}
            {showTransactionModal && (
                <TransactionPasswordModal 
                    onVerified={handleTransactionVerified}
                    onClose={() => {
                        setShowTransactionModal(false);
                        setPendingPasswordData(null);
                    }}
                    action="change your password"
                />
            )}

            <div className="cp-card">
                <div className="cp-header">
                    <h1>Change Password</h1>
                    <p>Secure your account with a new password</p>
                </div>

                {/* Error Alert */}
                {error && (
                    <div className="cp-alert cp-alert-error">
                        <span className="alert-icon">❌</span>
                        <div className="alert-content">
                            <strong>Error:</strong> {error}
                        </div>
                        <button 
                            className="alert-close"
                            onClick={() => setError('')}
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Success Alert */}
                {success && (
                    <div className="cp-alert cp-alert-success">
                        <span className="alert-icon">✅</span>
                        <div className="alert-content">
                            <strong>Success!</strong> {success}
                            <div className="alert-note">
                                You will be logged out in 3 seconds. Please login again with your new password.
                            </div>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="cp-form">
                    {/* Current Password */}
                    <div className="cp-form-group">
                        <label className="cp-label">
                            <span className="label-icon">🔒</span>
                            Current Password
                        </label>
                        <div className="cp-input-wrapper">
                            <input
                                type={showPassword.current ? 'text' : 'password'}
                                name="currentPassword"
                                value={formData.currentPassword}
                                onChange={handleChange}
                                className={`cp-input ${error && error.includes('Current password') ? 'cp-input-error' : ''}`}
                                placeholder="Enter your current password"
                                required
                                disabled={loading || success}
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="cp-eye-btn"
                                onClick={() => togglePasswordVisibility('current')}
                            >
                                {showPassword.current ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                    </div>

                    {/* New Password */}
                    <div className="cp-form-group">
                        <label className="cp-label">
                            <span className="label-icon">🔐</span>
                            New Password
                        </label>
                        <div className="cp-input-wrapper">
                            <input
                                type={showPassword.new ? 'text' : 'password'}
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleChange}
                                className="cp-input"
                                placeholder="Enter new password (min 6 characters)"
                                required
                                disabled={loading || success}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="cp-eye-btn"
                                onClick={() => togglePasswordVisibility('new')}
                            >
                                {showPassword.new ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        
                        {/* Password Strength Indicator */}
                        {formData.newPassword && (
                            <div className="cp-strength-meter">
                                <div className="strength-bar-container">
                                    <div 
                                        className="strength-bar"
                                        style={{
                                            width: `${(strength / 4) * 100}%`,
                                            backgroundColor: strengthColors[strength]
                                        }}
                                    ></div>
                                </div>
                                <div className="strength-text" style={{ color: strengthColors[strength] }}>
                                    Password Strength: {strengthLabels[strength]}
                                </div>
                                <ul className="strength-requirements">
                                    <li className={formData.newPassword.length >= 6 ? 'met' : ''}>
                                        ✓ At least 6 characters
                                    </li>
                                    <li className={/[A-Z]/.test(formData.newPassword) ? 'met' : ''}>
                                        ✓ At least one uppercase letter
                                    </li>
                                    <li className={/[0-9]/.test(formData.newPassword) ? 'met' : ''}>
                                        ✓ At least one number
                                    </li>
                                    <li className={/[^A-Za-z0-9]/.test(formData.newPassword) ? 'met' : ''}>
                                        ✓ At least one special character
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div className="cp-form-group">
                        <label className="cp-label">
                            <span className="label-icon">✓</span>
                            Confirm New Password
                        </label>
                        <div className="cp-input-wrapper">
                            <input
                                type={showPassword.confirm ? 'text' : 'password'}
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className={`cp-input ${formData.confirmPassword && formData.newPassword !== formData.confirmPassword ? 'cp-input-error' : ''}`}
                                placeholder="Confirm your new password"
                                required
                                disabled={loading || success}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="cp-eye-btn"
                                onClick={() => togglePasswordVisibility('confirm')}
                            >
                                {showPassword.confirm ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                            <div className="cp-error-hint">
                                <span>⚠️</span> Passwords do not match
                            </div>
                        )}
                    </div>

                    {/* Password Tips */}
                    <div className="cp-tips">
                        <h4>Password Tips:</h4>
                        <ul>
                            <li>Use a mix of uppercase and lowercase letters</li>
                            <li>Include numbers and special characters</li>
                            <li>Avoid using common words or personal information</li>
                            <li>Don't reuse passwords from other accounts</li>
                        </ul>
                    </div>

                    {/* Form Actions */}
                    <div className="cp-actions">
                        <button
                            type="button"
                            className="cp-btn cp-btn-secondary"
                            onClick={() => navigate('/admin/dashboard')}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="cp-btn cp-btn-primary"
                            disabled={loading || success || (formData.newPassword !== formData.confirmPassword && formData.confirmPassword !== '')}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    Processing...
                                </>
                            ) : (
                                'Change Password'
                            )}
                        </button>
                    </div>
                </form>

                {/* Security Note */}
                <div className="cp-security-note">
                    <div className="note-icon">🛡️</div>
                    <div className="note-content">
                        <strong>Security Note:</strong> After changing your password, you will be logged out
                        from all devices. You'll need to login again with your new password.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;